import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiAlertCircle } from '@lumapps/lumx/icons';
import { Emphasis, FlexBox, Icon, Orientation, SideNavigation, SideNavigationItem, Text } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { SETUP_SIDEBAR_ITEMS } from '../../constants';
import { useNavigation } from '../../hooks/useNavigation';
import { Menus } from '../../types';

import './index.scss';

/**
 * Props for the {@link SetupFormNavigation} component.
 */
export interface SetupFormNavigationProps {
    /** The key of the currently active menu. */
    activeMenuKey: Menus | undefined;
    /** A record of booleans indicating if a menu has an error. */
    errors: Record<Menus, boolean>;
    /** The menus to display. */
    menus: typeof SETUP_SIDEBAR_ITEMS;

    /** Callback when a menu is pressed. */
    onMenuPress: (subMenu: Menus) => void;
}

const CLASSNAME = 'setup-form-navigation';
const SCOPE = CLASSNAME;

/**
 * Renders the setup form navigation.
 *
 * @param SetupFormNavigationProps The component props.
 * @returns The rendered component.
 */
export const SetupFormNavigation: React.FC<SetupFormNavigationProps> = ({
    activeMenuKey,
    errors,
    menus,
    onMenuPress,
}) => {
    const { translateKey } = useTranslate();
    const { block, element } = useClassnames(CLASSNAME);
    const { getMenuIcon, getTranslatedKey } = useNavigation('settings');
    const { get } = useDataAttributes('microapps');

    const toggleButtonProps = React.useMemo(
        () => ({
            label: translateKey(GLOBAL.TOGGLE),
        }),
        [translateKey],
    );
    return (
        <SideNavigation className={block()}>
            {Object.entries(menus).map(([menuKey], menuItemIndex, menusArray) => {
                const typedMenuKey = menuKey as Menus; // type it so it's not `string`.

                const menuItemAttributes = get({
                    element: `${SCOPE}-${typedMenuKey}`,
                    action: 'toggle',
                    type: typedMenuKey,
                    position: menuItemIndex + 1,
                    count: menusArray.length,
                });
                const hasError = errors[typedMenuKey];

                return (
                    <SideNavigationItem
                        key={menuKey}
                        className={element('menu-item', { hasError })}
                        emphasis={Emphasis.medium}
                        icon={getMenuIcon(typedMenuKey)}
                        isSelected={activeMenuKey === typedMenuKey}
                        label={
                            <FlexBox
                                className={element('nav-label')}
                                orientation={Orientation.horizontal}
                                vAlign="space-between"
                                hAlign="center"
                                fillSpace
                            >
                                <Text as="span">{getTranslatedKey(typedMenuKey)}</Text>

                                <Icon className={element('nav-icon')} icon={mdiAlertCircle} />
                            </FlexBox>
                        }
                        toggleButtonProps={toggleButtonProps}
                        onClick={() => onMenuPress(typedMenuKey)}
                        {...menuItemAttributes}
                    />
                );
            })}
        </SideNavigation>
    );
};
